// ReceiptModal.tsx - Complete responsive receipt modal
import React from 'react';
import { PDFDownloadLink, PDFViewer } from "@react-pdf/renderer";
import AppointmentPaymentReceipt from './AppointmentPaymentReciept';

interface ReceiptModalProps {
  appointment: any;
  patientInfo: any;
  patientProfile: any;
  onClose: () => void;
}

const ReceiptModal: React.FC<ReceiptModalProps> = ({ appointment, patientInfo, patientProfile, onClose }) => {
  return (
    <>
      {/* Mobile View */}
      <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 p-4">
        <div className="block md:hidden relative bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden max-h-[80vh]">
          {/* Mobile Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-100 bg-gradient-to-r from-teal-50 to-teal-100">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-teal-600 rounded-xl flex items-center justify-center shadow-lg">
                <svg className="w-5 h-5 text-black fill-none stroke-current" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">Payment Receipt</h3>
                <p className="text-xs text-gray-600">Invoice Details</p>
              </div>
            </div>
            {/* Mobile Close Button */}
            <button
              onClick={onClose}
              className="w-8 h-8 text-gray-900 bg-gray-50 hover:bg-gray-100 rounded-full flex items-center justify-center transition-all duration-200"
            >
              <svg className="w-5 h-5 text-gray-800 fill-none stroke-current" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="p-6 space-y-6">
            {/* Patient Information */}
            <div className="bg-gray-50 rounded-xl p-4">
              <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                <svg className="w-5 h-5 mr-2 text-teal-600 fill-none stroke-current" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                Patient Details
              </h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Name</span>
                  <span className="font-medium text-gray-900">{patientProfile.patientName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Phone</span>
                  <span className="font-medium text-gray-900">{patientProfile.phoneNo}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Email</span>
                  <span className="font-medium text-gray-900">{patientProfile.email || 'N/A'}</span>
                </div>
              </div>
            </div>

            {/* Appointment Information */}
            <div className="bg-gray-50 rounded-xl p-4">
              <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                <svg className="w-5 h-5 mr-2 text-teal-600 fill-none stroke-current" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3a2 2 0 012-2h4a2 2 0 012 2v4m-6 9l6 6m0 0l6-6m-6 6V9a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2h6z" />
                </svg>
                Appointment Info
              </h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Date</span>
                  <span className="font-medium text-gray-900">{appointment.formatedDate}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Time</span>
                  <span className="font-medium text-gray-900">{appointment.formatedTime}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Doctor</span>
                  <span className="font-medium text-gray-900">{appointment.doctorName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Department</span>
                  <span className="font-medium text-gray-900">{appointment.specializationName}</span>
                </div>
              </div>
            </div>

            {/* Payment Information */}
            <div className="bg-teal-50 rounded-xl p-4 border border-teal-100">
              <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                <svg className="w-5 h-5 mr-2 text-teal-600 fill-none stroke-current" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                Payment Details
              </h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Amount</span>
                  <span className="font-bold text-teal-600 text-lg">{appointment.doctorFees}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Payment ID</span>
                  <span className="font-medium text-gray-900">{appointment.appointmentId}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Status</span>
                  <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                    Paid
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Mobile Download Button */}
          <div className="p-6 pt-0">
            <PDFDownloadLink
              document={<AppointmentPaymentReceipt appointment={appointment} onClose={onClose} patientInfo={patientInfo} />}
              fileName={`${patientProfile.patientName}-Invoice.pdf`}
            >
              {({ loading }) => (
                <button
                  className="w-full py-4 bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 text-white rounded-xl font-semibold text-base transition-all duration-200 shadow-lg active:scale-95 flex items-center justify-center space-x-2"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <svg className="w-5 h-5 animate-spin fill-none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75 fill-current" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      <span>Preparing...</span>
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5 fill-none stroke-current" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <span>Download Invoice</span>
                    </>
                  )}
                </button>
              )}
            </PDFDownloadLink>
          </div>

          {/* Mobile Footer */}
          <div className="px-6 pb-6">
            <div className="flex items-center justify-center space-x-2 text-xs text-gray-500 bg-gray-50 rounded-lg p-3">
              <svg className="w-4 h-4 fill-none stroke-current" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>Download to view complete invoice details</span>
            </div>
          </div>
        </div>
   

      {/* Tablet View */}
      <div className="hidden md:block lg:hidden relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
        {/* Tablet Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100 bg-gradient-to-r from-teal-50 to-teal-100">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-teal-600 rounded-2xl flex items-center justify-center shadow-lg">
              <svg className="w-6 h-6 text-white fill-none stroke-current" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-900">Payment Receipt</h3>
              <p className="text-sm text-gray-600">Invoice for {patientProfile.patientName}</p>
            </div>
          </div>
          {/* Tablet Close Button */}
          <button
            onClick={onClose}
            className="w-10 h-10 bg-gray-100 hover:bg-gray-200 rounded-xl flex items-center justify-center transition-all duration-200 hover:shadow-md"
          >
            <svg className="w-5 h-5 text-gray-600 fill-none stroke-current" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Tablet Receipt Details */}
        <div className="p-8">
          <div className="grid grid-cols-2 gap-6 mb-8">
            {/* Patient Information */}
            <div className="bg-gray-50 rounded-xl p-5">
              <h4 className="font-semibold text-gray-900 mb-4 flex items-center">
                <svg className="w-5 h-5 mr-2 text-teal-600 fill-none stroke-current" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                Patient Details
              </h4>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Name</span>
                  <span className="font-medium text-gray-900">{patientProfile.patientName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Phone</span>
                  <span className="font-medium text-gray-900">{patientProfile.phoneNo}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Email</span>
                  <span className="font-medium text-gray-900">{patientProfile.email || 'N/A'}</span>
                </div>
              </div>
            </div>

            {/* Appointment Information */}
            <div className="bg-gray-50 rounded-xl p-5">
              <h4 className="font-semibold text-gray-900 mb-4 flex items-center">
                <svg className="w-5 h-5 mr-2 text-teal-600 fill-none stroke-current" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3a2 2 0 012-2h4a2 2 0 012 2v4m-6 9l6 6m0 0l6-6m-6 6V9a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2h6z" />
                </svg>
                Appointment Info
              </h4>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Date</span>
                  <span className="font-medium text-gray-900">{appointment.formatedDate}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Time</span>
                  <span className="font-medium text-gray-900">{appointment.formatedTime}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Doctor</span>
                  <span className="font-medium text-gray-900">{appointment.doctorName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Department</span>
                  <span className="font-medium text-gray-900">{appointment.specializationName}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Payment Information - Full Width */}
          <div className="bg-teal-50 rounded-xl p-6 border border-teal-100 mb-8">
            <h4 className="font-semibold text-gray-900 mb-4 flex items-center">
              <svg className="w-5 h-5 mr-2 text-teal-600 fill-none stroke-current" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              Payment Details
            </h4>
            <div className="grid grid-cols-3 gap-6 text-sm">
              <div className="text-center">
                <span className="block text-gray-600 mb-1">Amount</span>
                <span className="font-bold text-teal-600 text-xl">{appointment.doctorFees}</span>
              </div>
              <div className="text-center">
                <span className="block text-gray-600 mb-1">Payment ID</span>
                <span className="font-medium text-gray-900">{appointment.appointmentId}</span>
              </div>
              <div className="text-center">
                <span className="block text-gray-600 mb-1">Status</span>
                <span className="inline-flex px-3 py-1 text-sm font-semibold rounded-full bg-green-100 text-green-800">
                  Paid
                </span>
              </div>
            </div>
          </div>

          {/* Tablet Download Button */}
          <div>
            <PDFDownloadLink
              document={<AppointmentPaymentReceipt appointment={appointment} onClose={onClose} patientInfo={patientInfo} />}
              fileName={`${patientProfile.patientName}-Invoice.pdf`}
            >
              {({ loading }) => (
                <button className="w-full py-4 bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 text-white rounded-xl font-semibold text-base transition-all duration-200 shadow-lg hover:shadow-xl flex items-center justify-center space-x-2">
                  {loading ? (
                    <>
                      <svg className="w-5 h-5 animate-spin fill-none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75 fill-current" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      <span>Preparing...</span>
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5 fill-none stroke-current" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <span>Download Invoice</span>
                    </>
                  )}
                </button>
              )}
            </PDFDownloadLink>
          </div>

          {/* Tablet Footer */}
          <div className="px-8 pb-8">
            <div className="flex items-center justify-center space-x-2 text-sm text-gray-500 bg-gray-50 rounded-lg p-4">
              <svg className="w-4 h-4 fill-none stroke-current" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>Download PDF to view complete invoice with official letterhead</span>
            </div>
          </div>
        </div>
      </div>

      {/* Desktop View */}
      <div className="hidden lg:block relative bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* Desktop Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100 bg-gradient-to-r from-teal-50 to-teal-100">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-teal-600 rounded-2xl flex items-center justify-center shadow-lg">
              <svg className="w-6 h-6 text-white fill-none stroke-current" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-900">Payment Receipt</h3>
              <p className="text-sm text-gray-600">Invoice for {patientProfile.patientName}</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            {/* Desktop Download Button */}
            <PDFDownloadLink
              document={<AppointmentPaymentReceipt appointment={appointment} onClose={onClose} patientInfo={patientInfo} />}
              fileName={`${patientProfile.patientName}-Invoice.pdf`}
            >
              {({ loading }) => (
                <button className="px-6 py-3 bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 text-white rounded-xl font-semibold text-sm transition-all duration-200 shadow-lg hover:shadow-xl flex items-center space-x-2">
                  {loading ? (
                    <>
                      <svg className="w-4 h-4 animate-spin fill-none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75 fill-current" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      <span>Preparing...</span>
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4 fill-none stroke-current" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <span>Download Invoice</span>
                    </>
                  )}
                </button>
              )}
            </PDFDownloadLink>
            {/* Desktop Close Button */}
            <button
              onClick={onClose}
              className="w-10 h-10 bg-gray-100 hover:bg-gray-200 rounded-xl flex items-center justify-center transition-all duration-200 hover:shadow-md"
            >
              <svg className="w-5 h-5 text-gray-600 fill-none stroke-current" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Desktop Content Area */}
        <div className="flex h-[calc(90vh-140px)]">
          {/* Right Side - PDF Viewer */}
          <div className="flex-1 bg-gray-50">
            <div className="w-full h-full p-4">
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 h-full overflow-hidden">
                <PDFViewer className="w-full h-full border-0 rounded-xl">
                  <AppointmentPaymentReceipt appointment={appointment} onClose={onClose} patientInfo={patientInfo} />
                </PDFViewer>
              </div>
            </div>
          </div>
        </div>

        {/* Desktop Footer */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 text-sm text-gray-500">
              <svg className="w-4 h-4 fill-none stroke-current" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>Receipt generated successfully. All payments are secure.</span>
            </div>
            <div className="text-xs text-gray-400">
              Generated on {new Date().toLocaleDateString()}
            </div>
          </div>
        </div>
      </div>
         </div>
    </>
  );
};

export default ReceiptModal;
